import { useState, useEffect } from 'react';
import {
  Calculator,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  TestTube,
  Save,
  Building,
  Store,
  FileText,
  Send,
  Info,
  RefreshCw,
  FileMinus2,
  Wrench,
} from 'lucide-react';
import {
  invoiceApi,
  storeApi,
  type Store as StoreType,
  type FakturowniaDepartment,
  type FakturowniaSettings,
} from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { ReconciliationPage } from './reconciliation/ReconciliationPage';


interface FakturowniaConfig {
  configured: boolean;
  isActive: boolean;
  settings: FakturowniaSettings;
  hasToken: boolean;
}

const defaultConfig: FakturowniaConfig = {
  configured: false,
  isActive: false,
  settings: {
    domain: '',
    autoSendToKsef: 'disabled',
    autoMarkAsPaid: false,
    deleteFromFakturownia: false,
    defaultPaymentDays: 14,
    defaultTaxRate: 23,
    defaultUnit: 'szt.',
    issueDateSource: 'today',
    sellDateSource: 'order_date',
    storeBindings: {},
    departments: [],
  },
  hasToken: false,
};

export function FakturowniaPage() {
  const [config, setConfig] = useState<FakturowniaConfig>(defaultConfig);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, storesRes] = await Promise.all([
        invoiceApi.getConfig(),
        storeApi.getAll(),
      ]);
      // Defensive: ensure settings exists with defaults
      const configData = configRes.data;
      if (!configData.settings) {
        configData.settings = {
          domain: '',
          autoSendToKsef: 'disabled',
          autoMarkAsPaid: false,
          deleteFromFakturownia: false,
          defaultPaymentDays: 14,
          defaultTaxRate: 23,
          defaultUnit: 'szt.',
          issueDateSource: 'today',
          sellDateSource: 'order_date',
                storeBindings: {},
          departments: [],
        };
      }
      setConfig(configData);
      setStores(storesRes.data || []);
    } catch (err) {
      console.error('[FakturowniaPage] Load error:', err);
      setError('Nie udało się pobrać konfiguracji');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSave = (newConfig: FakturowniaConfig) => {
    setConfig(newConfig);
  };

  const handleToggle = async () => {
    try {
      const { data } = await invoiceApi.toggle();
      setConfig((prev) => ({ ...prev, isActive: data.isActive }));
    } catch {
      setError('Nie udało się zmienić statusu integracji');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Integracja Fakturownia</h1>
        <p className="text-text-secondary mt-2">
          Automatyczne wystawianie faktur zgodnych z KSeF
        </p>
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
          <XCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Integration Status Card */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary">
              <Calculator className="w-6 h-6 text-dark-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Fakturownia</h2>
              <p className="text-sm text-text-secondary">
                {config.configured && config.settings?.domain
                  ? `Domena: ${config.settings.domain}.fakturownia.pl`
                  : 'Nie skonfigurowano'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {config.configured && (
              <div className="flex items-center gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    config.isActive
                      ? 'bg-success/20 text-success'
                      : 'bg-gray-700 text-text-secondary'
                  }`}
                >
                  {config.isActive ? 'Aktywna' : 'Nieaktywna'}
                </span>
                <button
                  onClick={handleToggle}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    config.isActive ? 'bg-success' : 'bg-surface-muted'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      config.isActive ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KSeF Info Banner */}
      <div className="card bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-warning/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-warning/20">
            <Info className="w-6 h-6 text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">KSeF - Ważna informacja</h3>
            <p className="text-text-secondary mt-1">
              Od <span className="text-warning font-medium">1 kwietnia 2026</span> KSeF (Krajowy System e-Faktur)
              będzie obowiązkowy dla wszystkich przedsiębiorców. Faktury wystawiane przez tę integrację
              są w pełni zgodne z wymaganiami KSeF.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      <FakturowniaConfigModal
        config={config}
        stores={stores}
        onSave={handleConfigSave}
      />
    </div>
  );
}

// Configuration Modal Component
function FakturowniaConfigModal({
  config,
  stores,
  onSave,
}: {
  config: FakturowniaConfig;
  stores: StoreType[];
  onSave: (config: FakturowniaConfig) => void;
}) {
  // Defensive: ensure settings exists with defaults
  const settings = config.settings || {
    domain: '',
    autoSendToKsef: 'disabled' as const,
    autoMarkAsPaid: false,
    deleteFromFakturownia: false,
    defaultPaymentDays: 14,
    defaultTaxRate: 23,
    defaultUnit: 'szt.',
    issueDateSource: 'today' as const,
    sellDateSource: 'order_date' as const,
    paymentDateSource: 'issue_date' as const,
    storeBindings: {},
    departments: [],
  };

  const [activeTab, setActiveTab] = useState<'api' | 'invoices' | 'ksef' | 'departments' | 'corrections' | 'repair'>('api');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // Form states
  const [apiToken, setApiToken] = useState('');
  const [domain, setDomain] = useState(settings.domain || '');
  const [showToken, setShowToken] = useState(false);

  // Invoice settings
  const [autoSendToKsef, setAutoSendToKsef] = useState(settings.autoSendToKsef || 'disabled');
  const [autoMarkAsPaid, setAutoMarkAsPaid] = useState(settings.autoMarkAsPaid ?? false);
  const [deleteFromFakturownia, setDeleteFromFakturownia] = useState(settings.deleteFromFakturownia ?? false);
  const [defaultPaymentDays, setDefaultPaymentDays] = useState(settings.defaultPaymentDays ?? 14);
  const [defaultTaxRate, setDefaultTaxRate] = useState(settings.defaultTaxRate ?? 23);
  const [defaultUnit, setDefaultUnit] = useState(settings.defaultUnit || 'szt.');
  const [issueDateSource, setIssueDateSource] = useState(settings.issueDateSource || 'today');
  const [sellDateSource, setSellDateSource] = useState(settings.sellDateSource || 'order_date');

  // Department bindings
  const [storeBindings, setStoreBindings] = useState<Record<string, number>>(
    settings.storeBindings || {}
  );
  // Normalize departments - handle old data format where each item might be wrapped in { department: {...} }
  // Also filter out null/undefined entries
  const normalizedDepartments: FakturowniaDepartment[] = (settings.departments || [])
    .filter((d: unknown) => d != null)
    .map((d: unknown) => {
      if (d && typeof d === 'object' && 'department' in d && (d as { department: FakturowniaDepartment }).department) {
        return (d as { department: FakturowniaDepartment }).department;
      }
      return d as FakturowniaDepartment;
    })
    .filter((d: FakturowniaDepartment) => d && d.id != null);
  const [departments, setDepartments] = useState<FakturowniaDepartment[]>(normalizedDepartments);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Correction settings state
  const [correctionDefaultReason, setCorrectionDefaultReason] = useState('Korekta do faktury - anulowanie zamówienia');
  const [correctionZeroQuantities, setCorrectionZeroQuantities] = useState(true);
  const [loadingCorrectionConfig, setLoadingCorrectionConfig] = useState(false);
  const [correctionConfigLoaded, setCorrectionConfigLoaded] = useState(false);

  // Auto-load correction settings when tab is opened
  useEffect(() => {
    if (activeTab === 'corrections' && !correctionConfigLoaded && !loadingCorrectionConfig) {
      const loadCorrectionConfig = async () => {
        setLoadingCorrectionConfig(true);
        try {
          const { data } = await invoiceApi.getCorrectionConfig();
          setCorrectionDefaultReason(data.defaultReason || 'Korekta do faktury - anulowanie zamówienia');
          setCorrectionZeroQuantities(data.zeroQuantities !== false);
          setCorrectionConfigLoaded(true);
        } catch {
          // Use defaults
          setCorrectionConfigLoaded(true);
        } finally {
          setLoadingCorrectionConfig(false);
        }
      };
      loadCorrectionConfig();
    }
  }, [activeTab, correctionConfigLoaded, loadingCorrectionConfig]);

  const tabs = [
    { id: 'api', label: 'API', icon: Settings },
    { id: 'invoices', label: 'Faktury', icon: FileText },
    { id: 'corrections', label: 'Korekty', icon: FileMinus2 },
    { id: 'ksef', label: 'KSeF', icon: Send },
    { id: 'departments', label: 'Działy', icon: Building },
    { id: 'repair', label: 'Serwis', icon: Wrench },
  ];

  const handleTestConnection = async () => {
    if (!apiToken || !domain) {
      setTestResult({ success: false, message: 'Wypełnij token API i domenę' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const { data } = await invoiceApi.testConnection({
        apiToken: apiToken || 'UNCHANGED',
        domain,
      });
      setTestResult({
        success: data.success,
        message: data.success
          ? `Połączono! Konto: ${data.accountName}`
          : data.error || 'Błąd połączenia',
      });
    } catch {
      setTestResult({ success: false, message: 'Błąd połączenia z serwerem' });
    } finally {
      setTesting(false);
    }
  };

  const handleLoadDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const { data } = await invoiceApi.getDepartments();
      setDepartments(data.departments || []);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const message = error.response?.data?.error || 'Nie udało się pobrać działów';
      setSaveResult({ success: false, message });
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);

    try {
      const { data } = await invoiceApi.saveConfig({
        apiToken: apiToken || 'UNCHANGED',
        domain,
        autoSendToKsef,
        autoMarkAsPaid,
        deleteFromFakturownia,
        defaultPaymentDays,
        defaultTaxRate,
        defaultUnit,
        issueDateSource,
        sellDateSource,
        storeBindings,
      });

      setSaveResult({ success: true, message: data.message });
      onSave({
        ...config,
        configured: true,
        isActive: true,
        settings: {
          domain,
          autoSendToKsef,
          autoMarkAsPaid,
          deleteFromFakturownia,
          defaultPaymentDays,
          defaultTaxRate,
          defaultUnit,
          issueDateSource,
          sellDateSource,
          storeBindings,
          departments,
        },
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setSaveResult({
        success: false,
        message: error.response?.data?.error || 'Błąd podczas zapisywania',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      {/* Tabs */}
      <div className="flex border-b border-border mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-[2px] ${
              activeTab === tab.id
                ? 'text-accent-primary border-accent-primary'
                : 'text-text-secondary border-transparent hover:text-text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* API Tab */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            {/* Domain */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Subdomena Fakturownia
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="twoja-firma"
                  className="input flex-1 rounded-r-none"
                />
                <span className="px-4 py-2.5 bg-surface-hover border border-l-0 border-border rounded-r-xl text-text-secondary">
                  .fakturownia.pl
                </span>
              </div>
              <p className="text-xs text-text-muted mt-1">
                Znajdziesz ją w adresie URL twojego konta Fakturownia
              </p>
            </div>

            {/* API Token */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Token API
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder={config.hasToken ? '••••••••••••••••' : 'Wklej token API'}
                  className="input pr-20"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="p-2 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {config.hasToken && !apiToken && (
                <p className="text-xs text-success mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Token zapisany
                </p>
              )}
            </div>

            {/* Test Connection */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleTestConnection}
                disabled={testing || (!apiToken && !config.hasToken) || !domain}
                className="btn-secondary flex items-center gap-2"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4" />
                )}
                Test połączenia
              </button>

              {testResult && (
                <div
                  className={`flex items-center gap-2 text-sm ${
                    testResult.success ? 'text-success' : 'text-danger'
                  }`}
                >
                  {testResult.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {testResult.message}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-6">
            {/* Issue Date Source */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Data wystawienia
              </label>
              <select
                value={issueDateSource}
                onChange={(e) => setIssueDateSource(e.target.value as typeof issueDateSource)}
                className="input"
              >
                <option value="today">Dzisiejsza data (moment wystawiania)</option>
                <option value="order_date">Data złożenia zamówienia</option>
              </select>
              <p className="text-xs text-text-muted mt-1">
                Jaka data ma być używana jako data wystawienia faktury
              </p>
            </div>

            {/* Sell Date Source */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Data sprzedaży
              </label>
              <select
                value={sellDateSource}
                onChange={(e) => setSellDateSource(e.target.value as typeof sellDateSource)}
                className="input"
              >
                <option value="order_date">Data złożenia zamówienia</option>
                <option value="shipment_date">Data nadania przesyłki</option>
                <option value="delivery_date">Data dostarczenia przesyłki</option>
                <option value="today">Dzisiejsza data</option>
              </select>
              <p className="text-xs text-text-muted mt-1">
                Jaka data ma być używana jako data sprzedaży na fakturze
              </p>
            </div>

            {/* Default Payment Days */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Termin płatności
              </label>
              <select
                value={defaultPaymentDays}
                onChange={(e) => setDefaultPaymentDays(parseInt(e.target.value))}
                className="input w-48"
              >
                <option value={0}>Płatne natychmiast (0 dni)</option>
                <option value={7}>7 dni</option>
                <option value={14}>14 dni</option>
                <option value={21}>21 dni</option>
                <option value={30}>30 dni</option>
                <option value={45}>45 dni</option>
                <option value={60}>60 dni</option>
                <option value={90}>90 dni</option>
              </select>
              <p className="text-xs text-text-muted mt-1">
                Ile dni od daty wystawienia faktury do terminu płatności
              </p>
            </div>

            {/* Default Tax Rate */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Domyślna stawka VAT (%)
              </label>
              <select
                value={defaultTaxRate}
                onChange={(e) => setDefaultTaxRate(parseInt(e.target.value))}
                className="input w-32"
              >
                <option value={23}>23%</option>
                <option value={8}>8%</option>
                <option value={5}>5%</option>
                <option value={0}>0%</option>
              </select>
            </div>

            {/* Default Unit */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Domyślna jednostka miary
              </label>
              <select
                value={defaultUnit}
                onChange={(e) => setDefaultUnit(e.target.value)}
                className="input w-40"
              >
                <option value="szt.">szt. (sztuka)</option>
                <option value="kg">kg (kilogram)</option>
                <option value="g">g (gram)</option>
                <option value="l">l (litr)</option>
                <option value="ml">ml (mililitr)</option>
                <option value="m">m (metr)</option>
                <option value="mb">mb (metr biezacy)</option>
                <option value="m2">m2 (metr kwadratowy)</option>
                <option value="m3">m3 (metr szescienny)</option>
                <option value="kpl.">kpl. (komplet)</option>
                <option value="op.">op. (opakowanie)</option>
                <option value="para">para</option>
                <option value="godz.">godz. (godzina)</option>
                <option value="usl.">usl. (usluga)</option>
              </select>
              <p className="text-xs text-text-muted mt-1">
                Jednostka miary wyświetlana przy ilości na fakturze
              </p>
            </div>

            {/* Auto mark as paid */}
            <div className="p-4 bg-surface-hover/30 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoMarkAsPaid}
                  onChange={(e) => setAutoMarkAsPaid(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <p className="text-text-primary font-medium">Automatycznie oznacz jako opłacone</p>
                  <p className="text-xs text-text-secondary mt-1">
                    Nowo wystawione faktury będą automatycznie oznaczane jako opłacone
                  </p>
                </div>
              </label>
            </div>

            {/* Delete from Fakturownia */}
            <div className="p-4 bg-surface-hover/30 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteFromFakturownia}
                  onChange={(e) => setDeleteFromFakturownia(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <div>
                  <p className="text-text-primary font-medium">Usuwaj również z Fakturownia</p>
                  <p className="text-xs text-text-secondary mt-1">
                    Przy usuwaniu faktury z Ecomflow, usuń ją również z Fakturownia (jeśli nie została wysłana do KSeF)
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* KSeF Tab */}
        {activeTab === 'ksef' && (
          <div className="space-y-6">
            {/* Auto Send to KSeF */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Automatyczne wysyłanie do KSeF
              </label>
              <select
                value={autoSendToKsef}
                onChange={(e) => setAutoSendToKsef(e.target.value as typeof autoSendToKsef)}
                className="input"
              >
                <option value="disabled">Wyłączone (ręczne wysyłanie)</option>
                <option value="pl_companies">Tylko polskie firmy (z NIP)</option>
                <option value="all_companies">Wszystkie firmy</option>
                <option value="all">Wszystkie faktury (włącznie z osobami fizycznymi)</option>
              </select>
              <p className="text-xs text-text-muted mt-1">
                Określa, które faktury mają być automatycznie wysyłane do KSeF po wystawieniu
              </p>
            </div>

            {/* KSeF Status Badges Legend */}
            <div className="p-4 bg-surface-hover/30 rounded-xl">
              <h4 className="text-sm font-medium text-text-primary mb-3">Statusy KSeF</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-500" />
                  <span className="text-text-secondary">Nie dotyczy</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="text-text-secondary">Oczekuje/W trakcie</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-success" />
                  <span className="text-text-secondary">Wysłano pomyślnie</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-danger" />
                  <span className="text-text-secondary">Błąd wysyłki</span>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-xl">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-warning font-medium">Uwaga</p>
                  <p className="text-text-secondary mt-1">
                    Po wysłaniu faktury do KSeF nie można jej usunąć ani edytować.
                    Można jedynie wystawić fakturę korygującą.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Departments Tab */}
        {activeTab === 'departments' && (
          <div className="space-y-6">
            {/* Load Departments Button */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-text-primary font-medium">Działy firmy</h4>
                <p className="text-xs text-text-secondary">
                  Przypisz działy z Fakturownia do swoich sklepów
                </p>
              </div>
              <button
                onClick={handleLoadDepartments}
                disabled={loadingDepartments || !config.configured}
                className="btn-secondary flex items-center gap-2"
              >
                {loadingDepartments ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Pobierz działy
              </button>
            </div>

            {/* Departments List */}
            {departments.length > 0 && (
              <div className="space-y-4">
                <div className="text-sm text-text-secondary">
                  Znaleziono {departments.length} działów
                </div>

                {/* Store to Department Bindings */}
                {stores.length > 0 && (
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-text-secondary">
                      Przypisz działy do sklepów
                    </h5>
                    {stores.map((store) => (
                      <div
                        key={store.id}
                        className="flex items-center gap-4 p-3 bg-surface-hover/30 rounded-xl"
                      >
                        <Store className="w-5 h-5 text-text-secondary" />
                        <span className="text-text-primary flex-1">{store.name}</span>
                        <select
                          value={storeBindings[store.id] || ''}
                          onChange={(e) =>
                            setStoreBindings((prev) => ({
                              ...prev,
                              [store.id]: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="input w-64"
                        >
                          <option value="">-- Wybierz dział --</option>
                          {departments.map((dept, idx) => (
                            <option key={dept.id ?? idx} value={dept.id ?? 0}>
                              {dept.shortcut || dept.name || `Dział ${dept.id}`} {dept.main && '(główny)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {departments.length === 0 && config.configured && (
              <EmptyState
                icon={Building}
                title='Kliknij "Pobierz działy" aby załadować listę'
                size="sm"
              />
            )}
          </div>
        )}

        {/* Corrections Tab */}
        {activeTab === 'corrections' && (
          <div className="space-y-6">
            {loadingCorrectionConfig && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
              </div>
            )}

            {correctionConfigLoaded && (
              <>
                {/* Info about numbering */}
                <div className="p-4 bg-surface-hover/50 rounded-lg border border-border">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-accent-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-text-secondary">
                        <strong>Numeracja korekt</strong> jest automatycznie pobierana z działu przypisanego do sklepu WooCommerce.
                        Korekta używa tego samego działu co oryginalna faktura, dzięki czemu numeracja jest spójna.
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        Przypisz sklepy do działów w zakładce "Działy" aby kontrolować numerację.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Default Correction Reason */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Domyślny powód korekty
                  </label>
                  <textarea
                    value={correctionDefaultReason}
                    onChange={(e) => setCorrectionDefaultReason(e.target.value)}
                    className="input"
                    rows={3}
                    placeholder="np. Korekta do faktury - anulowanie zamówienia"
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    Tekst wstawiany jako powód korekty (max 256 znaków dla KSeF)
                  </p>
                </div>

                {/* Zero Quantities */}
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={correctionZeroQuantities}
                      onChange={(e) => setCorrectionZeroQuantities(e.target.checked)}
                      className="w-5 h-5 rounded border-border bg-surface-hover text-success focus:ring-success"
                    />
                    <div>
                      <span className="text-text-primary">Wyzeruj ilości sztuk</span>
                      <p className="text-xs text-text-muted">
                        Korekta wyzeruje ilości wszystkich pozycji (standardowe zachowanie)
                      </p>
                    </div>
                  </label>
                </div>

                {/* Save Correction Settings Button */}
                <div className="pt-4 border-t border-border">
                  <button
                    onClick={async () => {
                      setSaving(true);
                      setSaveResult(null);
                      try {
                        await invoiceApi.saveCorrectionConfig({
                          series: 'auto', // Series is now determined by department
                          defaultReason: correctionDefaultReason,
                          zeroQuantities: correctionZeroQuantities,
                        });
                        setSaveResult({ success: true, message: 'Ustawienia korekt zapisane' });
                      } catch {
                        setSaveResult({ success: false, message: 'Nie udało się zapisać ustawień korekt' });
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Zapisz ustawienia korekt
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'repair' && (
          <ReconciliationPage />
        )}
      </div>

      {/* Save Button */}
      <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
        {saveResult && (
          <div
            className={`flex items-center gap-2 text-sm ${
              saveResult.success ? 'text-success' : 'text-danger'
            }`}
          >
            {saveResult.success ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            {saveResult.message}
          </div>
        )}
        <div className="flex-1" />
        <button
          onClick={handleSave}
          disabled={saving || !domain}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Zapisz konfigurację
        </button>
      </div>
    </div>
  );
}

export default FakturowniaPage;
